import { initializeStrapiBackend } from '../Contentful_API/strapiBackend';
import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Fab, Paper, Typography, IconButton, TextField, 
  Select, MenuItem, InputAdornment, CircularProgress, Chip, Button
} from '@material-ui/core';
import ChatIcon from '@material-ui/icons/Chat';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import PersonIcon from '@material-ui/icons/Person';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import ReactMarkdown from 'react-markdown';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import StaffLogin from './StaffLogin';
import ChatHistory from './ChatHistory';
import LiaMascot from '../Lia/LiaMascot';
import { LIA, LIA_GREETINGS } from '../Lia/liaBrand';
import { knownUnreachable } from '../../subdomains/useSubdomainUrl';
import './dashboard.css';

function preprocessContent(text) {
  if (!text) return '';

  let processed = text;

  // Convert unicode bullets '•' or asterisks '*' at line start to standard markdown bullet list '- '
  processed = processed.replace(/^([ \t]*)[•*]\s+/gm, '$1- ');

  // Convert numbered lists '1. ', '2. ' at line start to standard markdown bullet list '- ' for clean dot points
  processed = processed.replace(/^([ \t]*)\d+\.\s+/gm, '$1- ');

  // Auto-link plain text URLs (http://, https://, or relative paths like /publicationdetails/...) not already formatted in markdown
  const rawUrlRegex = /(?<!\]\(|="|href=")(https?:\/\/[^\s<]+|\/(?:publicationdetails|tenderdetails|eventdetails)\/[^\s<]+)/g;
  processed = processed.replace(rawUrlRegex, (url) => {
    const trailingPunctuation = url.match(/[.,;:!?)]*$/)?.[0] || '';
    const cleanUrl = trailingPunctuation ? url.slice(0, -trailingPunctuation.length) : url;
    return `[${cleanUrl}](${cleanUrl})${trailingPunctuation}`;
  });

  return processed;
}

const markdownComponents = {
  a: ({ href, children }) => {
    const isExternal = /^https?:\/\//i.test(href);
    return (
      <a
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noreferrer' : undefined}
      >
        {children}
      </a>
    );
  },
  ul: ({ children }) => <ul>{children}</ul>,
  ol: ({ children }) => <ul>{children}</ul>,
  li: ({ children }) => <li>{children}</li>,
  p: ({ children }) => <p>{children}</p>,
  strong: ({ children }) => <strong>{children}</strong>,
};

/**
 * The last few turns of the conversation, sent with each question so that follow-ups such as
 * "explain the PDF" keep their meaning. The opening greeting is left out.
 */
export function recentHistory(messages, limit = 8) {
  return messages
    .slice(messages[0]?.role === 'bot' ? 1 : 0)
    .filter(message => typeof message.content === 'string' && message.content.trim())
    .slice(-limit)
    .map(message => ({ role: message.role === 'user' ? 'user' : 'assistant', content: message.content.slice(0, 4000) }));
}

function MessageContent({ content }) {
  const formattedText = preprocessContent(content);
  return (
    <ReactMarkdown components={markdownComponents}>
      {formattedText}
    </ReactMarkdown>
  );
}

const useStyles = makeStyles((theme) => ({
  fab: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 1000,
    background: 'linear-gradient(135deg, #143a66, #0e2748)',
    color: 'white',
    boxShadow: '0 8px 28px rgba(14, 39, 72, 0.24)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 18,
    height: 72,
    padding: '12px 18px',
    gap: 12,
    textTransform: 'none',
    '&:focus-visible': { outline: '3px solid #c8a02a', outlineOffset: 4 },
    [theme.breakpoints.down('xs')]: { right: 16, bottom: 16, height: 60, padding: '10px 14px' },
    '&:hover': {
      background: 'linear-gradient(135deg, #225491, #143a66)',
    }
  },
  launcherIcon: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 40, height: 40, borderRadius: 12,
    background: 'rgba(255,255,255,0.1)', color: '#f4e7c0', flexShrink: 0,
  },
  launcherText: { display: 'flex', flexDirection: 'column', textAlign: 'left', lineHeight: 1.4 },
  launcherTitle: { fontSize: 14, fontWeight: 700, letterSpacing: '0.01em' },
  launcherSubtitle: { fontSize: 11, color: '#d8e5f3', fontWeight: 400 },
  badge: { fontSize: 10, fontWeight: 700, padding: '3px 6px', borderRadius: 5, color: '#f4e7c0', border: '1px solid rgba(244,231,192,0.4)' },
  headerActions: { display: 'flex', alignItems: 'center', gap: 4 },
  headerButton: {
    color: 'rgba(255,255,255,0.9)',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
  },
  headerSubtitle: { fontSize: 11, color: '#d8e5f3', marginTop: 2 },
  securityNotice: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '10px 14px',
    borderBottom: '1px solid #ead9a1',
    backgroundColor: '#fffaf0',
    color: '#6b5310',
    fontSize: 12,
    lineHeight: 1.45,
  },
  securityIcon: {
    flexShrink: 0,
    marginTop: 1,
    color: '#a47b00',
    fontSize: 18,
  },
  footerNote: { display: 'block', textAlign: 'center', color: '#5b6878', fontSize: 10, marginTop: 10 },
  chatWindow: {
    position: 'fixed',
    bottom: 108,
    right: theme.spacing(4),
    width: 400,
    height: 'min(600px, calc(100dvh - 132px))',
    maxHeight: 'calc(100dvh - 132px)',
    minHeight: 0,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '18px',
    overflow: 'hidden',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    background: '#ffffff',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    transition: 'all 0.3s ease-in-out',
    '&:focus-within': {
      boxShadow: '0 24px 64px rgba(14, 39, 72, 0.24)',
    },
    '@media (min-width: 600px) and (max-height: 720px)': {
      bottom: 92,
      height: 'calc(100dvh - 116px)',
      maxHeight: 'calc(100dvh - 116px)',
    },
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100vw - 32px)',
      right: theme.spacing(2),
      bottom: 92,
      height: 'calc(100dvh - 116px)',
      maxHeight: 'calc(100dvh - 116px)',
    }
  },
  maximizedWindow: {
    top: theme.spacing(3),
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    width: 'auto',
    height: 'auto',
    maxHeight: 'none',
    borderRadius: 16,
    [theme.breakpoints.down('sm')]: {
      top: 0,
      right: 0,
      bottom: 0,
      width: '100vw',
      height: '100dvh',
      borderRadius: 0,
    },
  },
  standaloneWindow: {
    '&&': { position: 'fixed', inset: 0, width: '100%', height: '100dvh', maxHeight: 'none', borderRadius: 0 },
  },
  // Inside a page (the LIA page): part of the layout instead of floating over it.
  embeddedWindow: {
    '&&': {
      position: 'relative', inset: 'auto', right: 'auto', bottom: 'auto', zIndex: 1,
      width: '100%', height: 'min(760px, calc(100dvh - 140px))', minHeight: 520, maxHeight: 'none',
      boxShadow: '0 16px 48px rgba(14, 39, 72, 0.16)', border: '1px solid #d8e5f3',
    },
  },
  welcomeMascot: { display: 'block', margin: '0 auto 14px' },
  meetLia: { display: 'inline-block', marginTop: 18, fontSize: 13, fontWeight: 600, color: '#225491' },
  accessBar: {
    flexShrink: 0,
    padding: '10px 16px', display: 'flex', flexWrap: 'wrap', gap: 8,
    alignItems: 'center', borderBottom: '1px solid #d8e5f3', background: '#fff',
  },
  header: {
    flexShrink: 0,
    background: 'linear-gradient(135deg, #143a66, #225491)',
    padding: theme.spacing(2, 2.5),
    minHeight: 76,
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 600,
  },
  languageSelector: {
    color: 'white',
    '& .MuiSelect-icon': {
      color: 'white',
    },
    '&:before': {
      borderColor: 'rgba(255,255,255,0.5)',
    },
    '&:after': {
      borderColor: 'white',
    }
  },
  messagesArea: {
    minHeight: 0,
    flexGrow: 1,
    padding: theme.spacing(3, 3.5),
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    background: 'linear-gradient(180deg, #f8fafc 0%, #eef4fa 100%)',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  promptPanel: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(2),
    border: '1px solid #d8e5f3',
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.82)',
  },
  promptLabel: {
    display: 'block',
    marginBottom: theme.spacing(1.25),
    color: '#334155',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  promptList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  promptChip: {
    height: 'auto',
    minHeight: 32,
    borderColor: '#cbd5e1',
    backgroundColor: '#ffffff',
    color: '#143a66',
    '& .MuiChip-label': {
      padding: '7px 10px',
      whiteSpace: 'normal',
      lineHeight: 1.3,
    },
    '&:hover': {
      borderColor: '#225491',
      backgroundColor: '#eef4fa',
    },
  },
  messageRow: {
    display: 'flex',
    gap: theme.spacing(1),
    maxWidth: 'min(760px, 86%)',
  },
  userRow: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
  },
  botRow: {
    alignSelf: 'flex-start',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  userAvatar: {
    background: theme.palette.grey[200],
    color: theme.palette.grey[700],
  },
  botAvatar: {
    background: '#eef4fa',
    border: '1px solid #d8e5f3',
  },
  messageBubble: {
    padding: theme.spacing(1.5, 2.25),
    borderRadius: '16px',
    overflowWrap: 'anywhere',
    minWidth: 0,
    fontSize: '0.91rem',
    lineHeight: 1.55,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    '& p': {
      margin: '0 0 8px 0',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& ul, & ol': {
      margin: '6px 0 8px 0',
      paddingLeft: '20px',
      listStyleType: 'disc',
    },
    '& li': {
      marginBottom: '6px',
      lineHeight: 1.5,
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& strong': {
      fontWeight: 650,
      letterSpacing: '0.01em',
    },
    '& a': {
      color: 'inherit',
      fontWeight: 700,
      textDecoration: 'underline',
      textUnderlineOffset: 2,
      '&:hover': {
        opacity: 0.85,
      },
    },
  },
  userBubble: {
    background: '#f1f5f9',
    color: '#0f172a',
    borderTopRightRadius: '4px',
    '& li::marker': {
      color: '#143a66',
    },
  },
  botBubble: {
    background: 'linear-gradient(135deg, #143a66, #225491)',
    color: 'white',
    borderTopLeftRadius: '4px',
    '& li::marker': {
      color: '#f4e7c0',
    },
  },
  inputArea: {
    flexShrink: 0,
    padding: theme.spacing(2, 3, 2.5),
    background: '#ffffff',
    borderTop: '1px solid #e2e8f0',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5, 2, 2),
    },
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '24px',
      backgroundColor: '#f8fafc',
    }
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: '#64748b',
    fontSize: '0.8rem',
    padding: theme.spacing(1, 2),
  }
}));

/**
 * LIA, the Ministry's AI assistant. Default: a floating launcher and window. `standalone`: the
 * full-window /chatbot page (also the staff dashboard). `embedded`: always open inside a page.
 */
export default function ChatBot({ standalone = false, embedded = false }) {
  const classes = useStyles();
  const inPage = standalone || embedded;
  const [conversations, setConversations] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('mof-chat-history-v1') || '[]');
      return Array.isArray(saved) ? saved.filter(c => c && typeof c.id === 'string' && typeof c.title === 'string' && c.audience === 'public' && Array.isArray(c.messages) && c.messages.every(m => m && ['user', 'bot'].includes(m.role) && typeof m.content === 'string')) : [];
    } catch { return []; }
  });
  const [activeId, setActiveId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [storageError, setStorageError] = useState('');
  useEffect(() => {
    try {
      localStorage.setItem('mof-chat-history-v1', JSON.stringify(conversations.filter(c => c.audience === 'public')));
      setStorageError('');
    } catch { setStorageError('History could not be saved on this browser. Export chats to keep a copy.'); }
  }, [conversations]);
  const [isOpen, setIsOpen] = useState(inPage);
  const [audience, setAudience] = useState('public');
  const [session, setSession] = useState(null);
  const [accessError, setAccessError] = useState('');
  const requestPending = useRef(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [language, setLanguage] = useState('English');
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      content: LIA_GREETINGS.English
    }
  ]);
  const messagesEndRef = useRef(null);
  const suggestedPrompts = [
    'What are the current open tenders?',
    'How do I submit a bid?',
    'Where can I find procurement documents?',
    'What services does the Ministry provide?',
    'How can I contact the Ministry?',
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleLanguageChange = (e) => {
    setLanguage(e.target.value);
    if (!activeId) setMessages([{ role: 'bot', content: getGreeting(e.target.value) }]);
    else setConversations(prev => prev.map(c => c.id === activeId ? { ...c, language: e.target.value } : c));
  };

  const getGreeting = (lang) => LIA_GREETINGS[lang] || LIA_GREETINGS.English;

  const handleSend = async (prompt = input) => {
    if (!prompt.trim() || requestPending.current || (audience === 'staff' && !session)) return;
    requestPending.current = true;

    const userMessage = prompt.trim();
    setInput('');
    const id = activeId || Date.now() + '-' + Math.random().toString(36).slice(2);
    const nextMessages = [...messages, { role: 'user', content: userMessage }];
    setActiveId(id);
    setMessages(nextMessages);
    setConversations(prev => activeId
      ? prev.map(c => c.id === id ? { ...c, messages: nextMessages, updatedAt: Date.now(), language } : c)
      : [{ id, title: userMessage.slice(0, 80), messages: nextMessages, audience, language, updatedAt: Date.now(), pinned: false }, ...prev]);
    const appendReply = content => {
      const complete = [...nextMessages, { role: 'bot', content }];
      setMessages(complete);
      setConversations(prev => prev.map(c => c.id === id ? { ...c, messages: complete, updatedAt: Date.now() } : c));
    };
    setIsLoading(true);

    try {
      const { apiUrl: backendUrl } = session || await initializeStrapiBackend();
      const response = await fetch(`${backendUrl}${audience === 'staff' ? '/chat/staff' : '/chat'}`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', ...(audience === 'staff' ? { Authorization: `Bearer ${session.token}` } : {}) },
        body: JSON.stringify({ message: userMessage, language: language, history: recentHistory(messages) })
      });

      if (audience === 'staff' && (response.status === 401 || response.status === 403)) {
        setSession(null);
        setActiveId(null);
        setConversations(prev => prev.filter(c => c.audience !== 'staff'));
        setMessages([{ role: 'bot', content: getGreeting(language) }]);
        setAccessError('Your session expired or access was denied. Please sign in again or contact your administrator.');
        return;
      }

      if (!response.ok) {
        throw new Error(`Chat request failed (HTTP ${response.status})`);
      }
      
      const data = await response.json();
      if (typeof data.reply !== 'string' || !data.reply.trim()) {
        throw new Error('Chat response did not contain a reply');
      }
      
      appendReply(data.reply);
    } catch (error) {
      console.error("Chat error:", error);
      appendReply('Sorry, the chat service is currently unavailable. Please try again later.');
    } finally {
      setIsLoading(false);
      requestPending.current = false;
    }
  };

  const changeAudience = (next) => {
    if (requestPending.current) return;
    setActiveId(null);
    setConversations(prev => prev.filter(c => c.audience !== 'staff'));
    setAudience(next);
    setSession(null);
    setAccessError('');
    setInput('');
    setMessages([{ role: 'bot', content: getGreeting(language) }]);
  };

  const newChat = () => {
    if (requestPending.current) return;
    setActiveId(null); setInput('');
    setMessages([{ role: 'bot', content: getGreeting(language) }]);
    setHistoryOpen(false);
  };
  const selectChat = chat => {
    if (requestPending.current) return;
    setActiveId(chat.id); setMessages(chat.messages);
    setLanguage(['English', 'Tetum', 'Portuguese'].includes(chat.language) ? chat.language : 'English');
    setInput(''); setHistoryOpen(false);
  };
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {!inPage && <Fab
        className={classes.fab}
        variant="extended"
        onClick={() => {
          setIsOpen(!isOpen);
          if (isOpen) setIsMaximized(false);
        }}
        aria-label={isOpen ? "Close LIA, the MoF AI Assistant" : "Open LIA, the MoF AI Assistant"}
        aria-expanded={isOpen}
        aria-controls={isOpen ? "mof-assistant" : undefined}
      >
        <span className={classes.launcherIcon + " mof-launcherIcon"}>{isOpen ? <CloseIcon /> : <LiaMascot size={36} />}</span>
        <span className={classes.launcherText}>
          <span className={classes.launcherTitle}>{LIA.brand}</span>
          <span className={classes.launcherSubtitle}>{isOpen ? 'Close conversation' : LIA.shortTagline}</span>
        </span>
        <span className={classes.badge}>AI</span>
      </Fab>}

      {isOpen && (
        <Paper
          id="mof-assistant"
          role="region"
          aria-label="LIA, the MoF AI Assistant"
          className={`mof-dashboard ${inPage || isMaximized ? "mof-dashboard-wide" : ""} ${classes.chatWindow} ${isMaximized ? classes.maximizedWindow : ''} ${standalone ? classes.standaloneWindow : ''} ${embedded ? classes.embeddedWindow : ''}`}
          elevation={embedded ? 0 : 24}
        >
          <ChatHistory conversations={conversations.filter(c => c.audience === audience)} activeId={activeId}
            open={historyOpen} onClose={() => setHistoryOpen(false)} busy={isLoading}
            onNew={newChat} onSelect={selectChat} audience={audience} available={audience === 'public' || !!session}
            onUpdate={(id, change) => setConversations(prev => prev.map(c => c.id === id ? { ...c, ...change } : c))}
            onDelete={id => { setConversations(prev => prev.filter(c => c.id !== id)); if (activeId === id) newChat(); }} />
          <main className="mof-chat-main">
          <div className={classes.header + ' mof-chat-header'}>
            <IconButton aria-label="Open chat history" onClick={() => setHistoryOpen(true)} className="mof-history-toggle"><ChatIcon /></IconButton>
            <div className={classes.headerTitle + " mof-headerTitle"}>
              <span className={classes.launcherIcon + " mof-launcherIcon"}><LiaMascot size={34} /></span>
              <div>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{LIA.brand}</Typography>
                <div className={classes.headerSubtitle + " mof-headerSubtitle"}>Ministry of Finance · {LIA.shortTagline}</div>
              </div>
            </div>
            <div className={classes.headerActions}>
            <Select
              inputProps={{ "aria-label": "Conversation language" }}
              value={language}
              disabled={isLoading}
              onChange={handleLanguageChange}
              className={classes.languageSelector}
              disableUnderline
            >
              <MenuItem value="English">English</MenuItem>
              <MenuItem value="Tetum">Tetum</MenuItem>
              <MenuItem value="Portuguese">Português</MenuItem>
            </Select>
            {!standalone && <Button component="a" href="/chatbot" target="_blank" rel="noopener noreferrer" className={classes.headerButton}
              onClick={event => {
                event.preventDefault();
                window.open(`/chatbot?refresh=${Date.now()}`, '_blank', 'popup,width=1000,height=800,noopener,noreferrer');
              }}>Open in new window</Button>}
            {standalone && <Button className={classes.headerButton} disabled={isLoading}
              title="Fetch the latest dashboard. Staff will need to sign in again."
              onClick={() => window.location.assign('/chatbot?refresh=' + Date.now())}>Refresh dashboard</Button>}
            {standalone && <Button component="a" href="/" className={classes.headerButton}>Back to website</Button>}
            {!inPage && <IconButton
              size="small"
              className={classes.headerButton}
              aria-label={isMaximized ? 'Restore assistant window' : 'Maximize assistant window'}
              onClick={() => setIsMaximized(!isMaximized)}
            >
              {isMaximized ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>}
            {!inPage && <IconButton
              size="small"
              className={classes.headerButton}
              aria-label="Close assistant"
              onClick={() => {
                setIsOpen(false);
                setIsMaximized(false);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>}
            </div>
          </div>

          <div className={classes.accessBar + " mof-access-bar"}>
            <Button disabled={isLoading} variant={audience === 'public' ? 'contained' : 'outlined'} color="primary" aria-pressed={audience === 'public'} onClick={() => changeAudience('public')}>General public</Button>
            <Button disabled={isLoading} variant={audience === 'staff' ? 'contained' : 'outlined'} color="primary" aria-pressed={audience === 'staff'} onClick={() => changeAudience('staff')}>Ministry / Agency</Button>
            {session ? <><span style={{ overflowWrap: 'anywhere' }}>Signed in as {session.email}</span><Button disabled={isLoading} onClick={() => changeAudience('staff')}>Sign out</Button></> : audience === 'public' && <span>Ask about public Ministry content. No sign-in required.</span>}
          </div>

          <div className={classes.securityNotice} role="note">
            <WarningOutlinedIcon className={classes.securityIcon} aria-hidden="true" />
            <span>
              Do not enter passwords, financial details, identity numbers or other sensitive information in this chat.
            </span>
          </div>

          {storageError && <div role="alert" className="mof-storage-error">{storageError}</div>}
          <div className={classes.messagesArea + " mof-messages"} role="log" aria-live="polite" aria-label="Conversation">
            {audience === 'staff' && !session ? <div>
              {accessError && <p role="alert">{accessError}</p>}
              <StaffLogin onLogin={value => { setSession(value); setAccessError(''); }} />
            </div> : <>
            {messages.length === 1 && !isLoading && (
              <div className={classes.promptPanel + ' mof-welcome'}>
                <LiaMascot size={88} className={classes.welcomeMascot} />
                <span className="mof-eyebrow">{`${LIA.name} · ${LIA.expansion}`}</span>
                <h1>How can I help you today?</h1>
                <p>{LIA.tagline} Find information, explore services, and get guidance in your language.</p>
                <span className={classes.promptLabel + " mof-promptLabel"}>Suggested questions</span>
                <div className={classes.promptList + " mof-promptList"}>
                  {suggestedPrompts.map((prompt) => (
                    <Chip
                      key={prompt}
                      className={classes.promptChip}
                      variant="outlined"
                      clickable
                      label={prompt}
                      onClick={() => handleSend(prompt)}
                    />
                  ))}
                </div>
                {/* lia.mof.gov.tl, unless this page already found it unreachable (e.g. on Timor Telecom). */}
                {!embedded && <a className={classes.meetLia} href={knownUnreachable('lia') ? `https://www.mof.gov.tl${LIA.path}` : LIA.url}>Meet LIA: what it can do and how it works →</a>}
              </div>
            )}
            {(messages.length === 1 ? [] : messages).map((msg, index) => (
              <div 
                key={index} 
                className={`mof-messageRow ${classes.messageRow} ${msg.role === 'user' ? classes.userRow : classes.botRow}`}
              >
                <div className={`${classes.avatar} ${msg.role === 'user' ? classes.userAvatar : classes.botAvatar}`}>
                  {msg.role === 'user' ? <PersonIcon fontSize="small" /> : <LiaMascot size={28} />}
                </div>
                <div className={`${msg.role === "user" ? "mof-userBubble" : "mof-botBubble"} ${classes.messageBubble} ${msg.role === 'user' ? classes.userBubble : classes.botBubble}`}>
                  <MessageContent content={msg.content} />
                </div>
              </div>
            ))}
            {isLoading && (
              <div className={`${classes.messageRow} ${classes.botRow}`}>
                <div className={`${classes.avatar} ${classes.botAvatar}`}>
                  <LiaMascot size={28} />
                </div>
                <div className={classes.loadingContainer}>
                  <CircularProgress size={16} />
                  <span>Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
            </>}
          </div>

          {(audience === 'public' || session) && <div className={classes.inputArea + " mof-composer"}>
            <TextField
              fullWidth
              multiline
              maxRows={6}
              variant="outlined"
              placeholder="Message LIA..."
              inputProps={{ "aria-label": "Your message" }}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              className={classes.inputField}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton 
                      color="primary"
                      aria-label="Send message"
                      onClick={() => handleSend()}
                      disabled={!input.trim() || isLoading}
                    >
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <span className={classes.footerNote}>Powered by AI. Do not share sensitive information. Please verify important information.</span>
          </div>}
          </main>
        </Paper>
      )}
    </>
  );
}
